import React, { FormEvent, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from '../../ui/toast';
import { Button } from '../../ui';
import { TextInput } from '../../ui/input/text_input/text_input';
import { AuthenticationUiLayout } from '../login_register/AuthenticationUILayout';
import { verifyMfa } from '../../utils/api_service/mfa_api';
import { AnimatedLoginSequence } from '../../assets/svgs/AnimateLogin';
import { finishSignIn } from '../../utils/auth/finishSignIn';
import { clearPending, takeToken, urlCarriesToken } from '../../utils/auth/mfaHandoff';

export const MfaChallenge = () => {
  const history = useHistory();
  // Password login leaves the challenge in sessionStorage; an SSO login hands
  // it over in the URL (?token=&next=). Either way the deep link is kept so
  // the user lands where they were headed once the code is accepted.
  const [{ token: challengeToken, next }] = useState(() => takeToken('challenge'));
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);

  useEffect(() => {
    // No pending challenge: send the user back to sign-in (in an effect, not
    // during render). Otherwise drop a URL-carried token from the address bar.
    if (!challengeToken) history.push('/login');
    else if (urlCarriesToken()) history.replace('/auth/mfa');
  }, [challengeToken, history]);
  if (!challengeToken) return null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const res = await verifyMfa(challengeToken, useRecovery ? { recoveryCode: code } : { code });
    const data = await res.json();
    if (!res.ok || !data.success) {
      toast.error('That code was not accepted. Please try again.', { position: 'bottom-center' });
      return;
    }
    clearPending('challenge');
    history.push(await finishSignIn(data, { deepLink: next }));
  };

  return (
    <AuthenticationUiLayout leftContent={<AnimatedLoginSequence />}>
      <form className='flex flex-col w-full gap-4 pt-10' onSubmit={submit}>
        <h2 className='text-2xl font-semibold text-slate-900'>Two-factor verification</h2>
        <p className='text-sm text-slate-500'>
          {useRecovery ? 'Enter one of your recovery codes.' : 'Enter the 6-digit code from your authenticator app.'}
        </p>
        <TextInput
            type='text'
            name='code'
            label={useRecovery ? 'Recovery code' : 'Authentication code'}
            value={code}
            onChange={(e) => setCode(e.target.value.trim())}
        />
        <Button type='submit' label='Verify' />
        <button type='button' className='text-xs text-slate-500 underline self-start'
                onClick={() => { setUseRecovery(!useRecovery); setCode(''); }}>
          {useRecovery ? 'Use an authenticator code instead' : 'Use a recovery code instead'}
        </button>
      </form>
    </AuthenticationUiLayout>
  );
};
